import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { HttpError, publicUrl } from "../api/client";
import {
  addSongToPlaylist,
  getLibrary,
} from "../api/playlists";
import { getSong } from "../api/songs";
import type { LibraryResponse, PlaylistSummary, SongDetail } from "../api/types";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { PlaylistAddButton } from "../components/PlaylistAddButton";
import { parseAssDialogue, type SubtitleCue } from "../lyrics/parseAss";
import { useNotifications } from "../notifications/useNotifications";

// Lazy so the media-player code does not ship in the main bundle; only
// users who land on a song detail page download it.
const SongPlayer = lazy(() => import("../components/SongPlayer"));

export interface SongPageProps {
  jobId: string;
  onBack: () => void;
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof HttpError) return err.toUserMessage();
  if (err instanceof Error) return err.message;
  return fallback;
}

interface SongPageShellProps {
  onBack: () => void;
  backLabel: string;
  children: ReactNode;
}

function SongPageShell({ onBack, backLabel, children }: SongPageShellProps) {
  return (
    <main className="mx-auto flex min-h-screen max-w-5xl flex-col gap-6 px-4 py-10">
      <header className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-stone-600 px-3 py-1.5 text-sm text-stone-200 transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
        >
          {backLabel}
        </button>
        <LanguageSwitcher />
      </header>
      {children}
    </main>
  );
}

export function SongPage({ jobId, onBack }: SongPageProps) {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotifications();
  const [song, setSong] = useState<SongDetail | null>(null);
  const [library, setLibrary] = useState<LibraryResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [pendingPlaylistSlug, setPendingPlaylistSlug] = useState<string | null>(null);
  const [cues, setCues] = useState<SubtitleCue[]>([]);
  const [previousJobId, setPreviousJobId] = useState(jobId);

  // Render-time state adjustment (mirrors `useJobPolling`): when navigating
  // between songs reset transient state synchronously so the next commit
  // observes the cleared values without an extra render pass.
  if (jobId !== previousJobId) {
    setPreviousJobId(jobId);
    setSong(null);
    setCues([]);
    setLoadError(null);
    setMutationError(null);
    setPendingPlaylistSlug(null);
  }

  // The data-fetch effect must not re-fire when the i18next `t` reference
  // changes (e.g. on language switch); otherwise the whole song + library +
  // subtitle download would replay. Read the current fallback message via a
  // ref instead — matches the pattern in `useJobPolling`.
  const loadErrorFallbackRef = useRef(t("songPage.loadError"));
  useEffect(() => {
    loadErrorFallbackRef.current = t("songPage.loadError");
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [detail, lib] = await Promise.all([getSong(jobId), getLibrary()]);
        if (cancelled) return;
        setSong(detail);
        setLibrary(lib);
        if (detail.subtitles_url) {
          const response = await fetch(publicUrl(detail.subtitles_url));
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const text = await response.text();
          if (!cancelled) setCues(parseAssDialogue(text));
        }
      } catch (err) {
        if (!cancelled) {
          const message = errorMessage(err, loadErrorFallbackRef.current);
          setLoadError(message);
          notifyError(message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, notifyError]);

  const addSongFallbackRef = useRef(t("home.errors.addSong"));
  useEffect(() => {
    addSongFallbackRef.current = t("home.errors.addSong");
  }, [t]);

  const handleAddToPlaylist = useCallback(
    async (playlistSlug: string) => {
      const playlistName =
        library?.playlists.find((playlist) => playlist.slug === playlistSlug)?.name ??
        playlistSlug;
      setPendingPlaylistSlug(playlistSlug);
      setMutationError(null);
      try {
        await addSongToPlaylist(playlistSlug, jobId);
        const refreshed = await getLibrary();
        setLibrary(refreshed);
        notifySuccess(
          t("notifications.addedToPlaylist", { playlist: playlistName }),
        );
      } catch (err) {
        const message = errorMessage(err, addSongFallbackRef.current);
        setMutationError(message);
        notifyError(message);
      } finally {
        setPendingPlaylistSlug(null);
      }
    },
    [jobId, library, notifyError, notifySuccess, t],
  );

  if (loadError) {
    return (
      <SongPageShell onBack={onBack} backLabel={t("songPage.back")}>
        <div
          role="alert"
          className="rounded-2xl border border-red-500 bg-red-500/10 p-4 text-sm text-red-100"
        >
          {loadError}
        </div>
      </SongPageShell>
    );
  }

  if (!song) {
    return (
      <SongPageShell onBack={onBack} backLabel={t("songPage.back")}>
        <p className="text-sm text-stone-400">{t("songPage.loading")}</p>
      </SongPageShell>
    );
  }

  const playlists: PlaylistSummary[] = library?.playlists ?? [];
  const sourcedVocals = song.vocals_audio_url ? publicUrl(song.vocals_audio_url) : null;
  const sourcedInstrumental = song.instrumental_audio_url
    ? publicUrl(song.instrumental_audio_url)
    : null;
  const stemsAvailable = sourcedVocals !== null && sourcedInstrumental !== null;

  return (
    <SongPageShell onBack={onBack} backLabel={t("songPage.back")}>
      <h1 className="text-3xl font-semibold text-stone-100">{song.title}</h1>

      {!stemsAvailable && (
        <p className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">
          {t("songPage.stemUnavailable")}
        </p>
      )}

      {stemsAvailable && (
        <Suspense
          fallback={
            <p className="text-sm text-stone-400">{t("songPage.playerLoading")}</p>
          }
        >
          <SongPlayer
            vocalsUrl={sourcedVocals}
            instrumentalUrl={sourcedInstrumental}
            durationSeconds={song.duration_seconds}
            cues={cues}
          />
        </Suspense>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-stone-100">
          {t("songPage.addToPlaylistHeading")}
        </h2>
        <div className="flex flex-wrap gap-2">
          {playlists.map((playlist) => {
            const alreadyAdded = playlist.songs.some(
              (entry) => entry.job_id === jobId,
            );
            return (
              <PlaylistAddButton
                key={playlist.slug}
                playlist={playlist}
                alreadyAdded={alreadyAdded}
                isPending={pendingPlaylistSlug === playlist.slug}
                onAdd={(slug) => void handleAddToPlaylist(slug)}
              />
            );
          })}
        </div>
      </section>

      {mutationError && (
        <div
          role="alert"
          className="rounded-2xl border border-red-500 bg-red-500/10 p-4 text-sm text-red-100"
        >
          {mutationError}
        </div>
      )}
    </SongPageShell>
  );
}
